import { Injectable } from '@angular/core';
import { filter, Observable, switchMap, BehaviorSubject } from 'rxjs';
import { CollectionAttributes, AdvisorResponse } from '../models/advisor.interface';
import { S3Service } from './s3.service';
import { BedrockService } from './bedrock.service';
import { HttpClient, HttpParams } from '@angular/common/http';
import { ConfigurationService } from './configuration.service';
import { DriftConfiguration } from '../models/configuration.interface';
import { ConversationHistory, SingleSessionResponse } from '../models/conversations.interface';

export interface PollingConfig {
  maxAttempts: number;
  baseInterval: number;
  maxInterval: number;
  backoffMultiplier: number;
  timeoutThreshold: number;
}

export interface PollingStatus {
  attempt: number;
  nextPollIn: number;
  totalElapsed: number;
  status: 'polling' | 'timeout' | 'complete' | 'error';
}

@Injectable({
  providedIn: 'root'
})
export class ConversationsService {
  currentPrompt = '';
  config: DriftConfiguration;

  private readonly defaultPollingConfig: PollingConfig = {
    maxAttempts: 150, // Increased from 30 to allow for longer processing
    baseInterval: 2000, // Start with 2 seconds
    maxInterval: 10000, // Cap at 10 seconds
    backoffMultiplier: 1.2, // Gradual increase
    timeoutThreshold: 300000 // 5 minutes total timeout
  };

  private pollingStatusSubject = new BehaviorSubject<PollingStatus | null>(null);
  public pollingStatus$ = this.pollingStatusSubject.asObservable();

  constructor(
    private http: HttpClient,
    private configurationService: ConfigurationService,
    private s3Service: S3Service,
    private bedrockService: BedrockService
  ) {
    this.config = {};

    this.configurationService
      .configurationLoaded()
      .pipe(filter(loaded => loaded === true))
      .subscribe(() => {
        this.config = this.configurationService.getConfiguration();
      });
  }

  public loadSession(session_id: string): Observable<SingleSessionResponse> {
    const params = new HttpParams().set('session_id', session_id);
    this.config = this.configurationService.getConfiguration();
    return this.http.get<SingleSessionResponse>(`${this.config.driftApi}/conversation`, { params });
  }

  public loadConversations(
    collection_id: string,
    selected_collections_id?: string[]
  ): Observable<ConversationHistory[]> {
    let params = new HttpParams().set('collection_id', collection_id);

    if (selected_collections_id && selected_collections_id.length > 0) {
      selected_collections_id.forEach(id => {
        params = params.append('selected_collections_id', id);
      });
    }

    this.config = this.configurationService.getConfiguration();
    return this.http.get<ConversationHistory[]>(`${this.config.driftApi}/conversations`, { params });
  }

  public sendMessage(
    model_id: string,
    collection_id: string,
    sessionId: string | null = null,
    message: string,
    activeFilters: CollectionAttributes,
    mult_collection_id?: string[]
  ): Observable<AdvisorResponse> {
    return this.bedrockService
      .invokeBedrockModelWithContext(sessionId, message, model_id, collection_id, activeFilters, mult_collection_id)
      .pipe(
        switchMap(response => {
          const filename = response.filename;
          this.currentPrompt = message;
          return this.pollForResponse(filename);
        })
      );
  }

  private pollForResponse(
    filename: string,
    config: PollingConfig = this.defaultPollingConfig
  ): Observable<AdvisorResponse> {
    let attempts = 0;
    let consecutiveNetworkErrors = 0;
    const startTime = Date.now();
    let cancelled = false;

    // Create an observable that polls until we get a complete response or exceed limits
    return new Observable<AdvisorResponse>(observer => {
      // Calculate next polling interval with exponential backoff
      const getNextInterval = (attempt: number): number => {
        const interval = Math.min(
          config.baseInterval * Math.pow(config.backoffMultiplier, attempt),
          config.maxInterval
        );
        return Math.floor(interval);
      };

      // Update polling status
      const updateStatus = (status: PollingStatus['status'], nextPollIn = 0) => {
        const totalElapsed = Date.now() - startTime;
        this.pollingStatusSubject.next({
          attempt: attempts,
          nextPollIn,
          totalElapsed,
          status
        });
      };

      // A single poll attempt
      const poll = () => {
        if (cancelled) {
          return;
        }

        const totalElapsed = Date.now() - startTime;

        // Check for timeout threshold
        if (totalElapsed >= config.timeoutThreshold) {
          updateStatus('timeout');
          observer.error(
            new Error(
              `Response timed out after ${config.timeoutThreshold}ms. The response may still be processing and will be available in History.`
            )
          );
          return;
        }

        // Check for max attempts
        if (attempts >= config.maxAttempts) {
          updateStatus('timeout');
          observer.error(
            new Error(
              `Response timed out after ${config.maxAttempts} polling attempts. The response may still be processing and will be available in History.`
            )
          );
          return;
        }

        attempts++;
        updateStatus('polling');

        this.s3Service.pollResult(filename).subscribe({
          next: response => {
            if (cancelled) {
              return;
            }

            // Reset consecutive network errors on successful response
            consecutiveNetworkErrors = 0;
            if (response) {
              console.log('Polling response status:', response.body.complete, 'total time', totalElapsed);
            }

            if (response && response.body?.complete === 'true') {
              const result: AdvisorResponse = {
                session_id: response.body.session_id,
                collection_id: response.body.collection_id,
                complete: response.body.complete,
                filename: filename,
                metrics: response.body.metrics || {},
                prompt: this.currentPrompt,
                response: response.body.response,
                source_documents: this.processSourceDocuments(response.body.source_documents),
                user: response.body.user
              };

              updateStatus('complete');
              observer.next(result);
              observer.complete();
            } else {
              // Not complete yet, schedule another poll with exponential backoff
              const nextInterval = getNextInterval(attempts);
              updateStatus('polling', nextInterval);
              setTimeout(poll, nextInterval);
            }
          },
          error: error => {
            if (cancelled) {
              return;
            }

            consecutiveNetworkErrors++;

            // Check if this is a network error that we should retry
            const isNetworkError = this.isRetryableNetworkError(error);

            if (isNetworkError && consecutiveNetworkErrors < 3) {
              // Retry with exponential backoff for network errors
              const retryInterval = getNextInterval(attempts) * consecutiveNetworkErrors;
              updateStatus('polling', retryInterval);
              setTimeout(poll, retryInterval);
            } else if (attempts >= config.maxAttempts || totalElapsed >= config.timeoutThreshold) {
              // Final timeout - provide helpful error message
              updateStatus('error');
              observer.error(
                new Error(
                  `Response timed out after ${attempts} attempts. The response may still be processing and will be available in History.`
                )
              );
            } else {
              // Continue polling for other types of errors (like 404 when file isn't ready yet)
              const nextInterval = getNextInterval(attempts);
              updateStatus('polling', nextInterval);
              setTimeout(poll, nextInterval);
            }
          }
        });
      };

      // Start polling
      poll();

      // Cancel polling if the subscription is unsubscribed
      return () => {
        cancelled = true;
        updateStatus('complete');
      };
    });
  }

  /**
   * Determines if an error is a retryable network error
   */
  private isRetryableNetworkError(error: { status?: number; name?: string; message?: string }): boolean {
    // Network connectivity issues
    if (error.status === 0) {
      return true;
    }

    // Server errors that might be temporary
    if (error.status !== undefined && error.status >= 500 && error.status < 600) {
      return true;
    }

    // Timeout errors
    if (error.name === 'TimeoutError' || error.message?.includes('timeout')) {
      return true;
    }

    return false;
  }

  /**
   * Get current polling status
   */
  public getPollingStatus(): Observable<PollingStatus | null> {
    return this.pollingStatus$;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private processSourceDocuments(documents: any[]): any[] {
    if (!documents || !Array.isArray(documents)) {
      return [];
    }

    return documents
      .map(doc => {
        if (doc.metadata && doc.metadata.source) {
          doc.metadata.source = doc.metadata.source.split('\\').pop().split('/').pop();
        }
        return doc;
      })
      .sort((a, b) => b.score - a.score);
  }
}
